import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useAuth } from '@/lib/auth-context';

export default function AccountTab() {
  const { user, account, accounts, role, leaveAccount } = useAuth();
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user || !account) return null;

  // The "Leave account" affordance only makes sense when the user
  // belongs to more than one account. The backend also refuses if
  // the user is the only admin — surface that error inline.
  const canLeave = accounts.length > 1;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2 space-y-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Identity
          </p>
          <CardTitle className="font-display text-base">Signed in as</CardTitle>
          <CardDescription>
            {user.fullName ? `${user.fullName} · ` : ''}
            {user.email}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-1 font-mono text-xs text-muted-foreground">
          <p>user · {user.id}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 space-y-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Account
          </p>
          <CardTitle className="font-display text-base">{account.name}</CardTitle>
          <CardDescription>
            Slug · {account.slug} · plan {account.plan}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-3 text-xs">
          <div className="font-mono text-muted-foreground space-y-1">
            <p>account · {account.id}</p>
            <p>your role · {role ?? '—'}</p>
          </div>
          {canLeave ? (
            <div className="pt-2 border-t border-rule">
              <p className="text-sm text-muted-foreground mb-2">
                You belong to {accounts.length} accounts. You can leave this one — you'll
                stay signed in to your other account{accounts.length > 2 ? 's' : ''}.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setError(null);
                  setConfirmLeave(true);
                }}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                data-testid="leave-account"
              >
                <LogOut className="h-4 w-4 mr-2" aria-hidden /> Leave this account
              </Button>
              {error ? (
                <p className="text-sm text-warning font-medium pt-2" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmLeave}
        onOpenChange={setConfirmLeave}
        title={`Leave ${account.name}?`}
        description="You'll lose access to this account's data immediately. The owner can re-invite you later if you change your mind. Your other accounts are unaffected."
        confirmLabel="Leave account"
        busyLabel="Leaving…"
        tone="destructive"
        testId="confirm-leave-account"
        onConfirm={async () => {
          try {
            await leaveAccount();
            setConfirmLeave(false);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not leave account');
            setConfirmLeave(false);
          }
        }}
      />
    </div>
  );
}
